import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Paperclip, Send, CheckCircle, Clock, AlertCircle, X, FileText, Image as ImageIcon, File } from 'lucide-react';
import api from '../../services/api';

const STATUS_LABEL = { aberto: 'Aberto', em_andamento: 'Em andamento', resolvido: 'Resolvido' };
const STATUS_COLOR = {
  aberto: 'bg-yellow-100 text-yellow-700',
  em_andamento: 'bg-blue-100 text-blue-700',
  resolvido: 'bg-green-100 text-green-700',
};

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function AnexoIcon({ tipo }) {
  if (tipo === 'pdf') return <FileText size={14} className="text-red-500" />;
  if (tipo === 'imagem') return <ImageIcon size={14} className="text-blue-500" />;
  return <File size={14} className="text-gray-400" />;
}

const CRM_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

function Anexo({ a }) {
  const href = `${CRM_BASE}${a.url}`;
  return (
    <a href={href} target="_blank" rel="noreferrer"
      className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded-md border border-blue-100">
      <AnexoIcon tipo={a.tipo} />
      {a.nome_arquivo}
    </a>
  );
}

function StatusBadge({ status }) {
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABEL[status] || status}</span>;
}

// ─── Detalhe do chamado ───────────────────────────────────────────────────────
function DetalheChamado({ id, onBack }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [texto, setTexto] = useState('');
  const [arquivos, setArquivos] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['chamado', id],
    queryFn: () => api.get(`/chamados/${id}`).then(r => r.data.data),
  });

  const mudarStatus = useMutation({
    mutationFn: (status) => api.patch(`/chamados/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries(['chamado', id]); qc.invalidateQueries(['chamados']); },
  });

  const enviar = async (e) => {
    e.preventDefault();
    if (!texto.trim() && !arquivos.length) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('texto', texto);
      arquivos.forEach(f => fd.append('anexos', f));
      await api.post(`/chamados/${id}/responder`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setTexto('');
      setArquivos([]);
      qc.invalidateQueries(['chamado', id]);
      qc.invalidateQueries(['chamados']);
    } finally { setEnviando(false); }
  };

  if (isLoading) return <div className="text-center py-16 text-gray-400">Carregando...</div>;
  if (!data) return null;

  const c = data;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h2 className="font-bold text-gray-900 text-base">{c.assunto}</h2>
          <p className="text-xs text-gray-400">Aberto em {formatDate(c.created_at)}</p>
        </div>
        <StatusBadge status={c.status} />
      </div>

      {/* Info do cliente */}
      <div className="bg-blue-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><p className="text-xs text-gray-500 font-medium">Cliente</p><p className="font-semibold text-gray-800">{c.cliente_nome}</p></div>
        <div><p className="text-xs text-gray-500 font-medium">CPF</p><p className="font-semibold text-gray-800">{c.cliente_cpf}</p></div>
        <div><p className="text-xs text-gray-500 font-medium">Empreendimento</p><p className="font-semibold text-gray-800">{c.empreendimento || '—'}</p></div>
        <div><p className="text-xs text-gray-500 font-medium">Unidade</p><p className="font-semibold text-gray-800">{c.bloco ? `${c.bloco}-${c.unidade}` : (c.unidade || '—')}</p></div>
      </div>

      {/* Thread */}
      <div className="space-y-3">
        {c.mensagens?.map(m => (
          <div key={m.id} className={`flex ${m.autor === 'construtora' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-xl px-4 py-3 shadow-sm ${m.autor === 'construtora' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-semibold ${m.autor === 'construtora' ? 'text-blue-100' : 'text-gray-500'}`}>
                  {m.autor === 'construtora' ? 'Inbase Construtora' : c.cliente_nome}
                </span>
                <span className={`text-xs ${m.autor === 'construtora' ? 'text-blue-200' : 'text-gray-400'}`}>{formatDate(m.created_at)}</span>
              </div>
              {m.texto && <p className="text-sm leading-relaxed whitespace-pre-line">{m.texto}</p>}
              {m.anexos?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {m.anexos.map(a => <Anexo key={a.id} a={a} />)}
                </div>
              )}
            </div>
          </div>
        ))}
        {(!c.mensagens || c.mensagens.length === 0) && (
          <div className="text-center py-8 text-gray-400 text-sm">Nenhuma mensagem ainda</div>
        )}
      </div>

      {/* Formulário de resposta */}
      {c.status !== 'resolvido' && (
        <form onSubmit={enviar} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <label className="block text-sm font-semibold text-gray-700">Responder ao cliente</label>
          <textarea
            rows={4}
            value={texto}
            onChange={e => setTexto(e.target.value)}
            placeholder="Digite sua resposta..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          {arquivos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {arquivos.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-md text-xs text-gray-700">
                  <Paperclip size={12} />
                  {f.name}
                  <button type="button" onClick={() => setArquivos(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 justify-between">
            <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
              <Paperclip size={14} /> Anexar arquivo
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,video/*" className="hidden" onChange={e => setArquivos(prev => [...prev, ...Array.from(e.target.files)])} />
            <button type="submit" disabled={enviando || (!texto.trim() && !arquivos.length)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Send size={14} /> {enviando ? 'Enviando...' : 'Responder'}
            </button>
          </div>
        </form>
      )}

      {/* Ações de status */}
      <div className="flex gap-2 justify-end">
        {c.status !== 'resolvido' && (
          <button onClick={() => mudarStatus.mutate('resolvido')}
            className="flex items-center gap-1.5 text-sm text-green-700 border border-green-200 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100 transition-colors">
            <CheckCircle size={14} /> Marcar como resolvido
          </button>
        )}
        {c.status === 'resolvido' && (
          <button onClick={() => mudarStatus.mutate('em_andamento')}
            className="flex items-center gap-1.5 text-sm text-blue-700 border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors">
            <Clock size={14} /> Reabrir chamado
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Lista de chamados ────────────────────────────────────────────────────────
export default function Chamados() {
  const [detalheId, setDetalheId] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('');

  const { data: chamados = [], isLoading } = useQuery({
    queryKey: ['chamados', filtroStatus],
    queryFn: () => api.get('/chamados', { params: filtroStatus ? { status: filtroStatus } : {} }).then(r => r.data.data),
    refetchInterval: 30000,
  });

  if (detalheId) return <DetalheChamado id={detalheId} onBack={() => setDetalheId(null)} />;

  const abertos = chamados.filter(c => c.pendente).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chamados</h1>
          {abertos > 0 && <p className="text-sm text-yellow-600 font-medium mt-0.5">{abertos} aguardando resposta</p>}
        </div>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Todos</option>
          <option value="aberto">Aberto</option>
          <option value="em_andamento">Em andamento</option>
          <option value="resolvido">Resolvido</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : chamados.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-gray-500 font-medium">Nenhum chamado encontrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">CPF</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Empreendimento</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Unidade</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Assunto</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chamados.map(c => (
                <tr key={c.id} onClick={() => setDetalheId(c.id)}
                  className="hover:bg-blue-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {c.pendente && <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" title="Aguardando resposta" />}
                      <span className="font-medium text-gray-800">{c.cliente_nome}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{c.cliente_cpf}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.empreendimento || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                    {c.bloco ? `${c.bloco}-${c.unidade}` : (c.unidade || '—')}
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{c.assunto}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-gray-400 text-xs hidden sm:table-cell">{formatDate(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
